/** Shared document list + detail fixtures for the HERO Dokumente module. */

import type { Translate } from "@/lib/i18n/translate";
import { ld } from "@/lib/theme/light-dark";

export type DocKind = "text" | "table" | "image";
export type DocPreviewKind = "pdf" | "xlsx" | "docx" | "jpg";
export type DocDetailTab =
  | "details"
  | "ocr"
  | "metadata"
  | "versions"
  | "actions";

/** Stable identity: the display name is translated, so it cannot be the key. */
export type DocId = "invoice" | "receivables" | "handover" | "damage";

export type DocItem = {
  readonly id: DocId;
  readonly name: string;
  readonly badge: string;
  readonly badgeColor: string;
  readonly icon: "file-text" | "file-spreadsheet" | "image";
  readonly iconColor: string;
  readonly kind: DocKind;
  readonly previewKind: DocPreviewKind;
};

type DocBase = {
  readonly badge: string;
  readonly color: string;
  readonly icon: DocItem["icon"];
  readonly previewKind: DocPreviewKind;
};

const PDF: DocBase = {
  badge: "PDF",
  color: ld("#D8443E", "#fb8276"),
  icon: "file-text",
  previewKind: "pdf",
};
const XLS: DocBase = {
  badge: "XLSX",
  color: ld("#1E8E5A", "#5dc38b"),
  icon: "file-spreadsheet",
  previewKind: "xlsx",
};
const DOC: DocBase = {
  badge: "DOCX",
  color: ld("#2B6BB2", "#6eaefb"),
  icon: "file-text",
  previewKind: "docx",
};
const IMG: DocBase = {
  badge: "JPG",
  color: ld("#7A6CB0", "#ac9fe7"),
  icon: "image",
  previewKind: "jpg",
};

function mk(id: DocId, name: string, base: DocBase, kind: DocKind): DocItem {
  return {
    id,
    name,
    badge: base.badge,
    badgeColor: base.color,
    icon: base.icon,
    iconColor: base.color,
    kind,
    previewKind: base.previewKind,
  };
}

/**
 * One showcase document per file type — grid + detail fixtures stay in sync.
 * `t` — `useTranslations("marketing.moduleDokumenteData")`.
 */
export function documents(t: Translate): readonly DocItem[] {
  return [
    mk("invoice", t("dateiRechnungHausmeister"), PDF, "text"),
    mk("receivables", t("dateiDebitorenliste"), XLS, "table"),
    mk("handover", t("dateiWohnungsuebergabe"), DOC, "text"),
    mk("damage", t("dateiSchadenKueche"), IMG, "image"),
  ];
}

export const DOCUMENT_COUNT: number = 4;

export type DocTag = { readonly label: string; readonly color: string };
export type DocContact = { readonly name: string; readonly initials: string };
export type DocProperty = { readonly name: string };
export type DocUnit = { readonly name: string };
export type DocVorgang = { readonly id: string; readonly title: string };
export type DocVersion = {
  readonly label: string;
  readonly date: string;
  readonly current?: boolean;
};
export type DocActionEntry = {
  readonly label: string;
  readonly date: string;
  readonly initials: string;
};

export type DocDetailFixture = {
  readonly title: string;
  readonly issuedAt: string | null;
  readonly tags: readonly DocTag[];
  readonly contacts: readonly DocContact[];
  readonly properties: readonly DocProperty[];
  readonly units: readonly DocUnit[];
  readonly vorgang: DocVorgang | null;
  readonly ocrLines: readonly string[];
  readonly metadata: readonly {
    readonly key: string;
    readonly value: string;
  }[];
  readonly versions: readonly DocVersion[];
  readonly actionLog: readonly DocActionEntry[];
};

const TAG_GREEN = "#3A9E6B";
const TAG_TEAL = "#008070";
const TAG_BLUE = "#3F6CB0";
const TAG_PURPLE = "#6D5BA6";

function fixtures(t: Translate): Readonly<Record<DocId, DocDetailFixture>> {
  return {
    invoice: {
      title: t("rechnungTitel"),
      issuedAt: "15.11.2025",
      tags: [{ label: t("tagRechnungen"), color: TAG_GREEN }],
      contacts: [{ name: t("hausmeisterSchmidt"), initials: "HS" }],
      properties: [{ name: t("ahornweg5") }],
      units: [],
      vorgang: { id: "1842", title: t("vorgangTreppenhausreinigung") },
      ocrLines: [
        t("rechnungOcr1"),
        t("rechnungOcr2"),
        t("rechnungOcr3"),
        t("rechnungOcr4"),
      ],
      metadata: [
        { key: t("metaDateigroesse"), value: "248 KB" },
        { key: t("metaMimeTyp"), value: "application/pdf" },
        { key: t("metaHochgeladen"), value: "16.11.2025, 09:14" },
        { key: t("metaErstelltVon"), value: "N. Hoffmann" },
      ],
      versions: [
        { label: t("version2"), date: "16.11.2025", current: true },
        { label: t("version1"), date: "15.11.2025" },
      ],
      actionLog: [
        { label: t("aktionImVorgang1842"), date: "16.11.", initials: "NH" },
        { label: t("aktionTagRechnungen"), date: "16.11.", initials: "NH" },
        { label: t("aktionHochgeladen"), date: "15.11.", initials: "HS" },
      ],
    },
    receivables: {
      title: t("debitorenlisteTitel"),
      issuedAt: "01.07.2026",
      tags: [{ label: t("tagBuchhaltung"), color: TAG_TEAL }],
      contacts: [],
      properties: [{ name: t("alleLiegenschaften") }],
      units: [],
      vorgang: null,
      ocrLines: [t("debitorenOcr1"), t("debitorenOcr2"), t("debitorenOcr3")],
      metadata: [
        { key: t("metaDateigroesse"), value: "86 KB" },
        {
          key: t("metaMimeTyp"),
          value:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        { key: t("metaHochgeladen"), value: "01.07.2026, 08:02" },
        { key: t("metaErstelltVon"), value: "N. Hoffmann" },
      ],
      versions: [{ label: t("version1"), date: "01.07.2026", current: true }],
      actionLog: [
        {
          label: t("aktionExportiertBuchhaltung"),
          date: "01.07.",
          initials: "NH",
        },
        { label: t("aktionHochgeladen"), date: "01.07.", initials: "NH" },
      ],
    },
    handover: {
      title: t("uebergabeTitel"),
      issuedAt: null,
      tags: [{ label: t("tagVertraege"), color: TAG_BLUE }],
      contacts: [{ name: t("petraSommer"), initials: "PS" }],
      properties: [{ name: t("ahornweg5") }],
      units: [{ name: t("we12") }],
      vorgang: null,
      ocrLines: [t("uebergabeOcr1"), t("uebergabeOcr2"), t("uebergabeOcr3")],
      metadata: [
        { key: t("metaDateigroesse"), value: "42 KB" },
        {
          key: t("metaMimeTyp"),
          value:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        },
        { key: t("metaHochgeladen"), value: "28.06.2026, 14:30" },
        { key: t("metaErstelltVon"), value: "N. Hoffmann" },
      ],
      versions: [{ label: t("version1"), date: "28.06.2026", current: true }],
      actionLog: [
        { label: t("aktionBearbeitet"), date: "28.06.", initials: "NH" },
        { label: t("aktionErstellt"), date: "28.06.", initials: "NH" },
      ],
    },
    damage: {
      title: t("schadenTitel"),
      issuedAt: "22.06.2026",
      tags: [{ label: t("tagSchadenmeldung"), color: TAG_PURPLE }],
      contacts: [{ name: t("petraSommer"), initials: "PS" }],
      properties: [{ name: t("ahornweg5") }],
      units: [{ name: t("we12") }],
      vorgang: { id: "1798", title: t("vorgangWasserschadenKueche") },
      ocrLines: [t("schadenOcr1")],
      metadata: [
        { key: t("metaDateigroesse"), value: "1,2 MB" },
        { key: t("metaMimeTyp"), value: "image/jpeg" },
        { key: t("metaHochgeladen"), value: "22.06.2026, 11:05" },
        { key: t("metaErstelltVon"), value: "P. Sommer" },
      ],
      versions: [{ label: t("version1"), date: "22.06.2026", current: true }],
      actionLog: [
        { label: t("aktionImVorgang1798"), date: "22.06.", initials: "NH" },
        { label: t("aktionHochgeladen"), date: "22.06.", initials: "PS" },
      ],
    },
  };
}

/** `t` — `useTranslations("marketing.moduleDokumenteData")`. */
export function getDocById(t: Translate, id: DocId): DocItem | undefined {
  return documents(t).find((doc) => doc.id === id);
}

/** `t` — `useTranslations("marketing.moduleDokumenteData")`. */
export function getDetailFixture(t: Translate, doc: DocItem): DocDetailFixture {
  return fixtures(t)[doc.id];
}

/** `t` — `useTranslations("marketing.moduleDokumenteData")`. */
export function detailTabs(t: Translate): readonly {
  readonly key: DocDetailTab;
  readonly label: string;
}[] {
  return [
    { key: "details", label: t("tabDetails") },
    { key: "ocr", label: t("tabOcrInhalt") },
    { key: "actions", label: t("tabAktionenverlauf") },
    { key: "versions", label: t("tabVersionen") },
    { key: "metadata", label: t("tabMetadaten") },
  ];
}
